import NoDataForSelection from 'components/atoms/NoDataForSelection';
import TileButton, { tileDataProp } from 'components/atoms/TileButton';
import MainSection from 'components/template/BumpReport/MainSection';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getConfig, resolveBumpReportOptionSelection } from 'utils/helperMethods';
import { commonClassName, commonHeaderClass } from './PromoScorecard';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    BUMP_REPORT_ABS_TAB_CLICKED,
    BUMP_REPORT_MAIN_TAB_CLICKED,
    BUMP_REPORT_VALUE_TAB_CLICKED,
} from 'utils/mixpanelEventNames';
import { Switch } from 'antd';
import { BumpReportFromType } from 'types/common';
import { CustomSelect } from './GrowthAnalysis';
import DownSvg from 'assets/svg/Down';

const defaultFrequencyDropdownList = [
    { label: 'Week', value: 'W', key: 'W' },
    { label: 'Month', value: 'M', key: 'M' },
    { label: 'Quarter', value: 'Q', key: 'Q' },
];

// export const groupByKey = 'groupBy';
// const bumpReportTileData = [
//     { key: 'increments', label: 'Increments' },
//     { key: 'promo', label: 'Promo' },
//     { key: groupByKey, label: 'Group By' },
// ];

const BumpReport = ({ from = 'bump-report' }: { from: BumpReportFromType }) => {
    const { appConfig } = useContext(UserContext);
    const isShipmentBumpReport = from === 'shipment-bump-report';
    const bumpReportConfig = getConfig(isShipmentBumpReport ? 'shipmentBumpReport' : 'bumpReport', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const volGroupByOptionsMapping = bumpReportConfig.volGroupByOptionsMapping;
    const { absTileData, volTileData, bumpReportTileData, groupByKey } = bumpReportConfig;
    const { filterNullError, availableFrequencies, allDimAndAttributeTypeList } = useContext(FilterContextData);
    const [bumpReportTile, setBumpReportTile] = useState(bumpReportTileData[0]);
    const [absTile, setAbsTile] = useState(absTileData[0]);
    const [volTile, setVolTile] = useState(volTileData[0]);
    const bumpChartY2AxisOptions = bumpReportConfig.bumpChartY2AxisOptions;
    const [loading, setLoading] = useState(false);
    const [groupByOptionSelected, setGroupByOptionSelected] = useState(volGroupByOptionsMapping[volTile.key][0]);
    const [optionSelected, setOptionSelected] = useState([bumpChartY2AxisOptions[volTile.key][0].value]);
    const [showAllLabels, setShowAllLabels] = useState(false);
    const [frequencyDropdownSelectedItem, setFrequencyDropdownSelectedItem] = useState<Record<string, any>>({});

    /**
     * Handles volTile change by preserving existing selections if they exist in new options,
     * otherwise sets the default value
     */
    const handleVolTileChange = useCallback(
        (item: tileDataProp) => {
            setGroupByOptionSelected(volGroupByOptionsMapping[item.key][0]);
            const newOptions = bumpChartY2AxisOptions[item.key] || [];
            setOptionSelected(resolveBumpReportOptionSelection(newOptions, optionSelected));
            setVolTile(item);
            trackEventMixpanel(BUMP_REPORT_VALUE_TAB_CLICKED, { tabName: item.label });
        },
        [optionSelected, bumpChartY2AxisOptions, volGroupByOptionsMapping],
    );

    const tileDataMemoized = useMemo(() => {
        return {
            absTile,
            volTile,
            bumpReportTile,
        };
    }, [absTile, volTile, bumpReportTile]);

    const dropdownList = useMemo(() => {
        //disable frequencies that are not available in global filter selection
        const updatedFrequencyDropdownList = defaultFrequencyDropdownList.map((item: any) => {
            return {
                ...item,
                disabled: !availableFrequencies.includes(item?.value),
            };
        });
        //set default item as the first item that is not disabled
        const defaultItem = updatedFrequencyDropdownList.find((item: any) => !item?.disabled);
        setFrequencyDropdownSelectedItem(defaultItem ?? {});
        return updatedFrequencyDropdownList;
    }, [availableFrequencies]);
    return (
        <section className={`${commonClassName} bg-white`}>
            {filterNullError ? (
                <NoDataForSelection />
            ) : (
                <div>
                    <div className="flex justify-between items-center">
                        <div className="flex justify-start items-center gap-4">
                            <h2 className={commonHeaderClass}>{bumpReportConfig?.mainHeader}</h2>
                            <TileButton
                                disabled={loading}
                                data={bumpReportTileData}
                                activeKey={bumpReportTile.key}
                                onClick={(item: any) => {
                                    setBumpReportTile(item);
                                    //mixpanel event
                                    trackEventMixpanel(BUMP_REPORT_MAIN_TAB_CLICKED, {
                                        tabName: item.label,
                                    });
                                }}
                            />
                        </div>
                        <div className="flex items-center gap-4">
                            <div>
                                <span className="font-xs text-blackAlt mr-1">View</span>
                                <CustomSelect
                                    className="dropdown-height dropdown-width uniqSelBRGroupByDropdown"
                                    suffixIcon={<DownSvg />}
                                    value={frequencyDropdownSelectedItem?.value}
                                    loading={allDimAndAttributeTypeList?.loading || false}
                                    options={dropdownList}
                                    onChange={(val: any, item: any) => {
                                        setFrequencyDropdownSelectedItem(item);
                                    }}
                                />
                            </div>
                            <div className="flex items-center">
                                <span className="font-xs text-blackAlt mr-1">Show Label</span>
                                <Switch onChange={(val) => setShowAllLabels(val)} checked={showAllLabels} />
                            </div>
                            <TileButton
                                disabled={loading}
                                data={absTileData}
                                activeKey={absTile.key}
                                onClick={(item: tileDataProp) => {
                                    setAbsTile(item);
                                    //mixpanel event
                                    trackEventMixpanel(BUMP_REPORT_ABS_TAB_CLICKED, {
                                        tabName: item.label,
                                    });
                                }}
                            />
                            <TileButton
                                disabled={loading}
                                data={volTileData}
                                activeKey={volTile.key}
                                onClick={handleVolTileChange}
                            />
                        </div>
                    </div>
                    <MainSection
                        frequencyDropdownSelectedItem={frequencyDropdownSelectedItem}
                        from={from}
                        showAllLabels={showAllLabels}
                        setLoading={setLoading}
                        groupByOptionSelected={groupByOptionSelected}
                        setGroupByOptionSelected={setGroupByOptionSelected}
                        optionSelected={optionSelected}
                        setOptionSelected={setOptionSelected}
                        tileData={tileDataMemoized}
                    />
                </div>
            )}
        </section>
    );
};

export default BumpReport;
